import { useLocation, useNavigate } from "@tanstack/react-router";
import { createContext, useContext, useMemo } from "react";
import { z } from "zod";

export const calendarModalsSearchSchema = z.object({
  "calendar-item": z.string().optional(),
  "workout-item": z.string().optional(),
  "workflow-workout": z.string().optional(),
  workflow: z.string().optional(),
  "program-workout": z.string().optional(),
  program: z.string().optional(),
  "task-dialog": z.string().optional(),
  "task-dialog-mode": z.enum(["view", "edit"]).optional().catch(undefined),
  "workflow-task-dialog": z.string().optional(),
  comments: z.string().optional(),
});

export type CalendarModalsSearchParams = z.infer<
  typeof calendarModalsSearchSchema
>;

export type CalendarModalState =
  | { type: "closed" }
  | {
      type: "calendar-item";
      calendarItemId: string;
      workoutItemId: string | undefined;
    }
  | { type: "workflow-workout"; workflowId: string; programWorkoutId: string }
  | { type: "program-workout"; programId: string; programWorkoutId: string }
  | { type: "task-dialog"; calendarItemId: string; mode: "view" | "edit" }
  | { type: "workflow-task-dialog"; workflowScheduleItemId: string };

export interface CalendarModalActions {
  openCalendarItem: (id: string, workoutItemId?: string) => void;
  openWorkflowWorkout: (workflowId: string, programWorkoutId: string) => void;
  openProgramWorkout: (programId: string, programWorkoutId: string) => void;
  openTaskDialog: (calendarItemId: string, mode: "view" | "edit") => void;
  openWorkflowTaskDialog: (workflowScheduleItemId: string) => void;
  closeModal: () => void;
  transitionWorkflowToCalendarItem: (calendarItemId: string) => void;
}

interface CalendarModalsContextValue extends CalendarModalActions {
  modalState: CalendarModalState;
}

const CLEARED_MODAL_SEARCH: Partial<CalendarModalsSearchParams> = {
  "calendar-item": undefined,
  "workout-item": undefined,
  "workflow-workout": undefined,
  workflow: undefined,
  "program-workout": undefined,
  program: undefined,
  "task-dialog": undefined,
  "task-dialog-mode": undefined,
  "workflow-task-dialog": undefined,
};

const calendarModalsContext = createContext<CalendarModalsContextValue | null>(
  null,
);

const calendarModalActionsContext = createContext<CalendarModalActions | null>(
  null,
);

interface CalendarModalsProviderProps {
  children: React.ReactNode;
  athleteId?: string;
}

const athleteIdContext = createContext<string | undefined>(undefined);

export function CalendarModalsProvider(props: CalendarModalsProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const modalState = useMemo(() => {
    const search = location.search;
    const calendarItemId = search["calendar-item"];
    const workflowWorkoutId = search["workflow-workout"];
    const workflowId = search.workflow;
    const programWorkoutId = search["program-workout"];
    const programId = search.program;
    const taskDialogId = search["task-dialog"];
    const taskDialogMode = search["task-dialog-mode"] ?? "edit";
    const workflowTaskDialogId = search["workflow-task-dialog"];

    if (calendarItemId) {
      return {
        type: "calendar-item" as const,
        calendarItemId,
        workoutItemId: search["workout-item"],
      };
    }

    if (workflowWorkoutId && workflowId) {
      return {
        type: "workflow-workout" as const,
        workflowId,
        programWorkoutId: workflowWorkoutId,
      };
    }

    if (programWorkoutId && programId) {
      return {
        type: "program-workout" as const,
        programId,
        programWorkoutId,
      };
    }

    if (taskDialogId) {
      return {
        type: "task-dialog" as const,
        calendarItemId: taskDialogId,
        mode: taskDialogMode,
      };
    }

    if (workflowTaskDialogId) {
      return {
        type: "workflow-task-dialog" as const,
        workflowScheduleItemId: workflowTaskDialogId,
      };
    }

    return { type: "closed" as const };
  }, [location.search]);

  const actions = useMemo(() => {
    return createCalendarModalActions(navigate);
  }, [navigate]);

  const contextValue = useMemo(() => {
    return { modalState, ...actions };
  }, [modalState, actions]);

  return (
    <athleteIdContext.Provider value={props.athleteId}>
      <calendarModalActionsContext.Provider value={actions}>
        <calendarModalsContext.Provider value={contextValue}>
          {props.children}
        </calendarModalsContext.Provider>
      </calendarModalActionsContext.Provider>
    </athleteIdContext.Provider>
  );
}

function createCalendarModalActions(
  navigate: ReturnType<typeof useNavigate>,
): CalendarModalActions {
  function openModal(search: Partial<CalendarModalsSearchParams>) {
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => {
        return { ...prev, ...CLEARED_MODAL_SEARCH, ...search };
      },
    });
  }

  return {
    openCalendarItem(id, workoutItemId) {
      openModal({ "calendar-item": id, "workout-item": workoutItemId });
    },
    openWorkflowWorkout(workflowId, programWorkoutId) {
      openModal({ "workflow-workout": programWorkoutId, workflow: workflowId });
    },
    openProgramWorkout(programId, programWorkoutId) {
      openModal({ "program-workout": programWorkoutId, program: programId });
    },
    openTaskDialog(calendarItemId, mode) {
      openModal({ "task-dialog": calendarItemId, "task-dialog-mode": mode });
    },
    openWorkflowTaskDialog(workflowScheduleItemId) {
      openModal({ "workflow-task-dialog": workflowScheduleItemId });
    },
    closeModal() {
      openModal({});
    },
    transitionWorkflowToCalendarItem(calendarItemId) {
      openModal({ "calendar-item": calendarItemId });
    },
  };
}

export function useCalendarModals(): CalendarModalsContextValue {
  const context = useContext(calendarModalsContext);

  if (!context) {
    throw new Error(
      "useCalendarModals must be used within a CalendarModalsProvider",
    );
  }

  return context;
}

export function useCalendarModalActions(): CalendarModalActions {
  const context = useContext(calendarModalActionsContext);

  if (!context) {
    throw new Error(
      "useCalendarModalActions must be used within a CalendarModalsProvider",
    );
  }

  return context;
}

export function useCalendarModalsAthleteId(): string | undefined {
  return useContext(athleteIdContext);
}
